/** Último segmento del nombre AcroForm (p. ej. `...NombreCompleto[0]` → `NombreCompleto`). */
export function acroFormFieldBaseSegment(fieldName: string): string {
	const s = (fieldName || '').trim();
	if (!s) {
		return '';
	}
	const last = s.split('.').pop() || s;
	return last.replace(/\[\d+\]/g, '').trim();
}

export function formDataKeyBase(key: string): string {
	return key.replace(/_required$/i, '').trim();
}

export function resolveAcroFormDataValue(
	formData: { [key: string]: any },
	fieldName: string
): { value: any; found: boolean } {
	if (!formData || typeof formData !== 'object') {
		return { value: undefined, found: false };
	}
	const fn = (fieldName || '').trim();
	if (!fn) {
		return { value: undefined, found: false };
	}
	const tryKey = (k: string): { value: any; found: boolean } | null => {
		if (Object.prototype.hasOwnProperty.call(formData, k)) {
			return { value: formData[k], found: true };
		}
		return null;
	};
	const orderedKeys: string[] = [fn, `${fn}_required`];
	if (/_required$/i.test(fn)) {
		orderedKeys.push(fn.replace(/_required$/i, ''));
	}
	const fnSeg = acroFormFieldBaseSegment(fn);
	if (fnSeg && fnSeg !== fn) {
		orderedKeys.push(fnSeg, `${fnSeg}_required`);
	}
	for (const k of orderedKeys) {
		const hit = tryKey(k);
		if (hit) {
			return hit;
		}
	}
	const widgetBase = acroFormFieldBaseSegment(fn);
	if (widgetBase) {
		for (const dataKey of Object.keys(formData)) {
			const storedSeg = acroFormFieldBaseSegment(
				formDataKeyBase(dataKey)
			);
			if (storedSeg === widgetBase) {
				return { value: formData[dataKey], found: true };
			}
		}
		for (const dataKey of Object.keys(formData)) {
			const storedSeg = acroFormFieldBaseSegment(
				formDataKeyBase(dataKey)
			);
			if (
				storedSeg.toLowerCase() === widgetBase.toLowerCase()
			) {
				return { value: formData[dataKey], found: true };
			}
		}
	}
	return { value: undefined, found: false };
}

/**
 * ngx-extended `formDataChange` usa nombres pdf.js; el backend guarda claves como `X_required`.
 * Fusiona sin sustituir todo el objeto (evita perder `*_required` al editar).
 */
export function mergeNgxPdfFormDataIntoStored(
	stored: { [key: string]: any } | null | undefined,
	ngxPayload: { [key: string]: any } | null | undefined
): { [key: string]: any } {
	const out: { [key: string]: any } =
		stored && typeof stored === 'object' ? { ...stored } : {};
	if (!ngxPayload || typeof ngxPayload !== 'object') {
		return out;
	}
	const allKeys = new Set([...Object.keys(out), ...Object.keys(ngxPayload)]);
	for (const k of Object.keys(ngxPayload)) {
		let v = ngxPayload[k];
		const prev = out[k];
		if (
			prev === true &&
			(v === '' || v === undefined || v === null)
		) {
			v = prev;
		}
		out[k] = v;
		const seg = acroFormFieldBaseSegment(k);
		if (!seg) {
			continue;
		}
		const canonical = [...allKeys].find((sk) => {
			const storedBase = formDataKeyBase(sk);
			return (
				storedBase === seg ||
				acroFormFieldBaseSegment(storedBase) === seg
			);
		});
		if (canonical) {
			const prevC = out[canonical];
			let vC = v;
			if (
				prevC === true &&
				(vC === '' || vC === undefined || vC === null)
			) {
				vC = prevC;
			}
			out[canonical] = vC;
		}
	}
	return out;
}

/** Valor que representa casilla/radio marcado (PdfFormJson o DOM). */
export function isTruthyPdfToggleValue(v: any): boolean {
	if (v === true) {
		return true;
	}
	if (typeof v === 'string') {
		const s = v.trim();
		if (!s) {
			return false;
		}
		return !/^(off|no|false|0)$/i.test(s);
	}
	return false;
}

/**
 * Lee el estado real de checkbox/radio en la capa de anotación (fuente de verdad visual).
 * Útil cuando getFormData() o formDataChange devuelven vacío o desfasados.
 */
export function mergeNgxPdfFormDataFromDomAnnotationLayer(
	base: { [key: string]: any } | null | undefined
): { [key: string]: any } {
	const out =
		base && typeof base === 'object' ? { ...base } : {};
	if (typeof document === 'undefined') {
		return out;
	}
	const patch: { [key: string]: any } = {};
	const nodes = document.querySelectorAll('.annotationLayer input');
	for (let i = 0; i < nodes.length; i++) {
		const el = nodes[i] as HTMLInputElement;
		const fn = (el.name || '').trim() || el.id;
		if (!fn) {
			continue;
		}
		const t = (el.type || '').toLowerCase();
		if (t === 'checkbox') {
			const on =
				el.checked &&
				String(el.value || '')
					.trim()
					.toLowerCase() !== 'off';
			patch[fn] = on
				? el.value && String(el.value).trim() !== ''
					? el.value
					: true
				: false;
		} else if (t === 'radio' && el.checked) {
			patch[fn] = el.value;
		}
	}
	return mergeNgxPdfFormDataIntoStored(out, patch);
}

/**
 * Tras fusionar con getFormData(), restaura valores marcados si el snapshot quedó vacío/falso por error de ngx.
 */
export function reconcileTruthySnapshot(
	memory: { [key: string]: any },
	snapshot: { [key: string]: any }
): { [key: string]: any } {
	const out = { ...snapshot };
	if (!memory || typeof memory !== 'object') {
		return out;
	}
	for (const mk of Object.keys(memory)) {
		const mv = memory[mk];
		if (!isTruthyPdfToggleValue(mv)) {
			continue;
		}
		const memSeg = acroFormFieldBaseSegment(formDataKeyBase(mk));
		if (!memSeg) {
			continue;
		}
		for (const sk of Object.keys(out)) {
			const snapSeg = acroFormFieldBaseSegment(formDataKeyBase(sk));
			if (snapSeg !== memSeg) {
				continue;
			}
			const sv = out[sk];
			if (
				sv === false ||
				sv === '' ||
				sv === undefined ||
				sv === null
			) {
				out[sk] = mv;
			}
		}
	}
	return out;
}

/**
 * Interpreta el valor guardado / del servidor para un checkbox AcroForm.
 * Por defecto desmarcado si es ambiguo (alinea con PdfFormJson sin clave = no marcado).
 */
export function pdfCheckboxRawToChecked(
	raw: any,
	element: HTMLInputElement
): boolean {
	if (typeof raw === 'boolean') {
		return raw;
	}
	if (raw === false || raw === 'Off' || raw === 'off' || raw === '') {
		return false;
	}
	if (raw === true || raw === 'Yes' || raw === 'On' || raw === 'on') {
		return true;
	}
	if (raw === null || raw === undefined) {
		return false;
	}
	const s = String(raw).trim();
	if (
		s === '' ||
		s === 'Off' ||
		s === 'off' ||
		s === 'No' ||
		s === 'no' ||
		s === 'false' ||
		s === '0'
	) {
		return false;
	}
	if (
		s === 'Yes' ||
		s === 'On' ||
		s === 'on' ||
		s === 'true' ||
		s === '1'
	) {
		return true;
	}
	const exportOn = (element.value || '').trim();
	if (exportOn && s === exportOn) {
		return true;
	}
	// Estados "off" en PDF (Aparencia /AS) — todo lo demás no vacío suele ser export "on"
	if (isPdfCheckboxOffExport(s)) {
		return false;
	}
	if (s !== '') {
		return true;
	}
	return false;
}

function isPdfCheckboxOffExport(s: string): boolean {
	return /^(off|no|false|0)$/i.test(s.trim());
}

/**
 * Aplica PdfFormJson a un input radio: el backend guarda a veces `true`/`false` mientras
 * el widget usa export "Yes"/"Off" (o idioma local).
 */
export function pdfRadioRawToChecked(raw: any, element: HTMLInputElement): boolean {
	if (raw == null || raw === '') {
		return false;
	}
	const ev = String(element.value || '').trim();
	const sr = String(raw).trim();
	if (ev === sr) {
		return true;
	}
	if (raw === false || raw === 'Off' || raw === 'off') {
		return false;
	}
	if (raw === true) {
		return ev !== '' && !isPdfCheckboxOffExport(ev) && !/^no$/i.test(ev);
	}
	if (sr === 'true' || sr === '1') {
		return /^(yes|on|1|true)$/i.test(ev) || ev === 'true';
	}
	if (/^(yes|on|1|true)$/i.test(sr)) {
		return ev === sr || /^(yes|on|1)$/i.test(ev);
	}
	return ev === sr;
}

/**
 * Segundo pase: aplica toggles cuando resolveAcroFormDataValue no encontró clave pero hay
 * el mismo segmento en PdfFormJson (casos raros de nombre).
 */
export function forceApplyUnmatchedTruthyTogglesToDom(
	formData: { [key: string]: any }
): void {
	if (!formData || typeof document === 'undefined') {
		return;
	}
	const nodes = document.querySelectorAll('.annotationLayer input');
	for (let i = 0; i < nodes.length; i++) {
		const el = nodes[i] as HTMLInputElement;
		const fn = (el.name || '').trim() || el.id;
		if (!fn) {
			continue;
		}
		const t = (el.type || '').toLowerCase();
		const first = resolveAcroFormDataValue(formData, fn);
		if (first.found) {
			continue;
		}
		const widgetSeg = acroFormFieldBaseSegment(fn);
		if (!widgetSeg) {
			continue;
		}
		for (const dk of Object.keys(formData)) {
			if (!isTruthyPdfToggleValue(formData[dk])) {
				continue;
			}
			const seg = acroFormFieldBaseSegment(formDataKeyBase(dk));
			if (seg !== widgetSeg) {
				continue;
			}
			if (t === 'checkbox') {
				el.checked = pdfCheckboxRawToChecked(formData[dk], el);
			} else if (t === 'radio') {
				el.checked = pdfRadioRawToChecked(formData[dk], el);
			}
			el.dispatchEvent(new Event('input', { bubbles: true }));
			el.dispatchEvent(new Event('change', { bubbles: true }));
			break;
		}
	}
}

/** Resultado de NgxExtendedPdfViewerService.getFormData() → objeto plano por nombre de campo. */
export function ngxGetFormDataArrayToFlatObject(fields: any[]): { [key: string]: any } {
	const o: { [key: string]: any } = {};
	if (!Array.isArray(fields)) {
		return o;
	}
	for (const field of fields) {
		const name = field && field.name;
		if (!name) {
			continue;
		}
		const a = field.fieldAnnotation;
		if (!a) {
			o[name] = '';
			continue;
		}
		if (typeof a.isChecked === 'boolean') {
			o[name] = a.isChecked;
			continue;
		}
		if (a.checkBox && typeof a.checkBox.isChecked === 'boolean') {
			o[name] = a.checkBox.isChecked;
			continue;
		}
		if (a.radioButton && typeof a.radioButton.isChecked === 'boolean') {
			o[name] = a.radioButton.isChecked;
			continue;
		}
		if (
			a.buttonWidget &&
			typeof a.buttonWidget.isChecked === 'boolean'
		) {
			o[name] = a.buttonWidget.isChecked;
			continue;
		}
		if (a.value !== undefined && a.value !== null) {
			o[name] = a.value;
		} else {
			o[name] = '';
		}
	}
	return o;
}
